import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useAuth } from './AuthContext';

// Mirrors LEAVE_ROSTER_ELIGIBLE on the backend (server.js) - kept as a
// separate, explicit list rather than reusing EMAIL_TO_NAME (missing
// Iqbal/Hafis/Desmond) or approvers.json (missing Kash, and shared with
// actual approval-authority logic elsewhere - don't want to risk that
// file's shape for this unrelated feature). If this list ever needs to
// change, update both places.
// Level mirrors the backend's LEAVE_ROSTER_PEOPLE (server.js) - keep both
// in sync if this ever changes. Relief eligibility is same level or one
// level apart; enforced here for the dropdown, and again server-side
// since a direct API call could otherwise bypass a frontend-only check.
const ELIGIBLE_PEOPLE = [
  { name: 'Kash',    email: 'kashwini.segal@ninjavan.co', level: 1 },
  { name: 'Hafis',   email: 'hafis.karim@ninjavan.co',     level: 1 },
  { name: 'Iqbal',   email: 'iqbal.azmi@ninjavan.co',      level: 2 },
  { name: 'Kai Jun', email: 'kaijun.chong@ninjavan.co',    level: 3 },
  { name: 'Desmond', email: 'mar.thienho@ninjavan.co',     level: 4 },
];

const API_BASE = '/api/leave-roster';

// Sends via the same logged-in browser session used for CAS itself - the
// server-side attempt (nodemailer/OAuth2, then an Apps Script Web App call
// from the backend) both hit real walls tonight: gmail.send is a
// Google-restricted scope requiring app verification once "In production",
// and the Apps Script deployment is locked to "Anyone within Ninja Van"
// access by org policy, which requires an actual logged-in ninjavan.co
// session - something a server-side request never has, but the user's own
// browser already does, since they're logged in to use CAS at all.
const APPS_SCRIPT_SEND_URL = 'https://script.google.com/a/macros/ninjavan.co/s/AKfycbxLJi1lpB1lxkJL0wNR9STt8jr-TMxSKjvcmIjDPPzywvlmdpcAGHRq2YPD1PIw21Ve/exec';
const APPS_SCRIPT_SEND_KEY = 'NFS_SEND_2026_V3';

// Fired via a hidden iframe doing a plain GET, not fetch(). The Apps Script
// /exec URL gets re-requested repeatedly in some setups (a "storm" - seen
// with DevTools open on Console); doGet dedups on the id below so only one
// email goes out. Each send has a unique id: retries of one send share it
// and are absorbed, while a new send with identical content still goes
// through. The iframe is removed after 30s so a stray storm can't run
// indefinitely on a long-lived page (removal itself is untested).
export function sendNotificationEmail({ to, subject, text: fullText, iosBanner = false }) {
  try {
    // Body travels as a URL query parameter; very long URLs risk being rejected
    // somewhere along the way. The real limit isn't known - 1500 is a
    // conservative guess, not a measured figure.
    const MAX_TEXT = 1500;
    const text = fullText && fullText.length > MAX_TEXT
      ? fullText.slice(0, MAX_TEXT) + '\n\n[...truncated - open the Ninja Mart Credit System for the full text]'
      : fullText;
    const id = 'ls-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
    const qs = new URLSearchParams({ key: APPS_SCRIPT_SEND_KEY, to, subject, text, id }).toString();
    // iOS: Safari won't carry the Google login into an embedded iframe (a
    // top-level tab did work: 1 email, 1 execution). A tab opened from code after a
    // network call is usually blocked as a popup, so show a banner with a real link
    // instead - the rep's own tap is the gesture that lets it open. Only used when
    // the caller asks for it (iosBanner) - currently just a salesperson's credit-query
    // reply. The reply itself is already saved by now; this only sends the approver's
    // notification email. One-off: nothing records whether it was tapped or sent.
    // Attached to document.body so it survives route changes.
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    if (isIOS && iosBanner) {
      const bm = localStorage.getItem('cas_lang') === 'bm';
      let wrap = document.getElementById('notify-banners');
      if (!wrap) {
        wrap = document.createElement('div');
        wrap.id = 'notify-banners';
        wrap.style.cssText = 'position:fixed;left:12px;right:12px;bottom:calc(env(safe-area-inset-bottom,0px) + 12px);z-index:99999;display:flex;flex-direction:column;gap:8px;';
        document.body.appendChild(wrap);
      }
      const item = document.createElement('div');
      item.style.cssText = 'background:#fff;border:1px solid #c8102e;border-radius:10px;padding:10px 12px;box-shadow:0 4px 16px rgba(0,0,0,0.2);display:flex;align-items:center;gap:10px;font-size:13px;color:#333;';
      const msg = document.createElement('div');
      msg.style.cssText = 'flex:1;min-width:0;word-break:break-word;';
      msg.textContent = bm
        ? 'Balasan berjaya disimpan. Ketik untuk hantar notifikasi kepada pelulus.'
        : 'Reply saved. Tap to send a notification to the approver.';
      const link = document.createElement('a');
      link.href = APPS_SCRIPT_SEND_URL + '?' + qs;
      link.target = '_blank';
      link.rel = 'noopener';
      link.textContent = bm ? 'Hantar notifikasi' : 'Send notification';
      link.style.cssText = 'background:#c8102e;color:#fff;padding:8px 12px;border-radius:6px;text-decoration:none;font-weight:600;white-space:nowrap;';
      link.onclick = () => setTimeout(() => item.remove(), 300);
      const close = document.createElement('button');
      close.textContent = '\u00d7';
      close.style.cssText = 'background:none;border:none;font-size:20px;color:#888;padding:0 4px;';
      close.onclick = () => item.remove();
      item.appendChild(msg);
      item.appendChild(link);
      item.appendChild(close);
      wrap.appendChild(item);
      return;
    }
    const f = document.createElement('iframe');
    f.style.display = 'none';
    f.src = APPS_SCRIPT_SEND_URL + '?' + qs;
    document.body.appendChild(f);
    setTimeout(() => { try { f.remove(); } catch (e) { /* already gone */ } }, 30000);
  } catch (e) {
    console.warn('[LeaveRoster] notification email failed:', e.message);
  }
}

const fmtYMD = (d) => {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
};

const fmtDisplay = (ymd) => {
  if (!ymd) return '';
  const [y, m, d] = ymd.split('-');
  return `${d}/${m}/${y}`;
};

// Builds a 6-row x 7-col grid for the given month, padding with nulls for
// days outside the month so every week is a full row.
function buildMonthGrid(year, month) {
  const firstDay = new Date(year, month, 1);
  const startWeekday = firstDay.getDay(); // 0 = Sunday
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const cells = [];
  for (let i = 0; i < startWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const date = new Date(year, month, d);
    cells.push({ date, ymd: fmtYMD(date) });
  }
  while (cells.length % 7 !== 0) cells.push(null);

  const weeks = [];
  for (let i = 0; i < cells.length; i += 7) weeks.push(cells.slice(i, i + 7));
  return weeks;
}

// Every entry (any status except declined) covering this date - for the
// hover tooltip, regardless of who the currently-selected relief is.
function entriesOnDate(ymd, entries) {
  return entries.filter(e => e.start_date.slice(0, 10) <= ymd && e.end_date.slice(0, 10) >= ymd);
}

// Specifically: is the CURRENTLY SELECTED relief unavailable on this date -
// either on leave themselves, or already locked in covering someone else.
function blockingEntryForRelief(ymd, entries, reliefEmail) {
  if (!reliefEmail) return null;
  return entries.find(e => {
    const covers = e.start_date.slice(0, 10) <= ymd && e.end_date.slice(0, 10) >= ymd;
    if (!covers) return false;
    return e.user_email === reliefEmail || e.relief_email === reliefEmail;
  }) || null;
}

function LeaveCalendarGrid({ entries, reliefEmail, rangeStart, rangeEnd, onPick }) {
  const today = new Date();
  const [viewYear, setViewYear]   = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [hoverInfo, setHoverInfo] = useState(null); // { ymd, x, y, text }

  const weeks = useMemo(() => buildMonthGrid(viewYear, viewMonth), [viewYear, viewMonth]);
  const monthLabel = new Date(viewYear, viewMonth, 1).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

  const goPrev = () => { if (viewMonth === 0) { setViewYear(y => y - 1); setViewMonth(11); } else setViewMonth(m => m - 1); };
  const goNext = () => { if (viewMonth === 11) { setViewYear(y => y + 1); setViewMonth(0); } else setViewMonth(m => m + 1); };

  const inRange = (ymd) => rangeStart && rangeEnd && ymd >= rangeStart && ymd <= rangeEnd;
  const isRangeEnd = (ymd) => ymd === rangeStart || ymd === rangeEnd;

  return (
    <div style={{ border: '1px solid #ddd', borderRadius: 8, padding: 12, position: 'relative' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <button type="button" onClick={goPrev} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 16, color: '#c8102e', padding: '2px 8px' }}>‹</button>
        <div style={{ fontWeight: 600, fontSize: 13, color: '#333' }}>{monthLabel}</div>
        <button type="button" onClick={goNext} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: 16, color: '#c8102e', padding: '2px 8px' }}>›</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, fontSize: 11, color: '#888', textAlign: 'center', marginBottom: 4 }}>
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map(d => <div key={d}>{d}</div>)}
      </div>
      {weeks.map((week, wi) => (
        <div key={wi} style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 2, marginBottom: 2 }}>
          {week.map((cell, ci) => {
            if (!cell) return <div key={ci} />;
            const { ymd } = cell;
            const isPast = ymd < fmtYMD(today);
            const blocking = blockingEntryForRelief(ymd, entries, reliefEmail);
            const isBlocked = !!blocking || isPast;
            const selected = inRange(ymd);
            const edge = isRangeEnd(ymd);

            return (
              <div
                key={ci}
                onMouseEnter={(e) => {
                  const onLeave = entriesOnDate(ymd, entries);
                  if (onLeave.length > 0) {
                    const rect = e.currentTarget.getBoundingClientRect();
                    setHoverInfo({
                      ymd,
                      x: rect.left + rect.width / 2,
                      y: rect.top,
                      text: onLeave.map(o => `${o.user_name} on leave${o.relief_name ? ` (${o.relief_name} covering)` : ''}`).join(' · '),
                    });
                  }
                }}
                onMouseLeave={() => setHoverInfo(null)}
                onClick={() => { if (!isBlocked) onPick(ymd); }}
                style={{
                  textAlign: 'center', padding: '6px 0', borderRadius: 6, fontSize: 12,
                  cursor: isBlocked ? 'not-allowed' : 'pointer',
                  background: edge ? '#c8102e' : selected ? '#fce8e6' : isBlocked ? '#f1f1f1' : '#fff',
                  color: edge ? '#fff' : isBlocked ? '#bbb' : '#333',
                  textDecoration: isBlocked && blocking ? 'line-through' : 'none',
                  border: entriesOnDate(ymd, entries).length > 0 && !isBlocked ? '1px solid #f0a500' : '1px solid transparent',
                }}
              >
                {cell.date.getDate()}
              </div>
            );
          })}
        </div>
      ))}
      {hoverInfo && (
        <div style={{
          position: 'fixed', left: hoverInfo.x, top: hoverInfo.y - 8, transform: 'translate(-50%, -100%)',
          background: '#333', color: '#fff', fontSize: 11, padding: '4px 8px', borderRadius: 4,
          whiteSpace: 'nowrap', zIndex: 1000, pointerEvents: 'none',
        }}>
          {hoverInfo.text}
        </div>
      )}
      <div style={{ fontSize: 11, color: '#888', marginTop: 8 }}>
        {reliefEmail
          ? 'Greyed-out dates are unavailable for the chosen relief. Click a start date, then an end date.'
          : 'Choose a relief above to see their availability.'}
      </div>
    </div>
  );
}

function LeaveRosterModal({ onClose }) {
  const { user } = useAuth();
  const [entries, setEntries] = useState([]);
  const [mine, setMine] = useState([]);
  const [reliefEmail, setReliefEmail] = useState('');
  const [rangeStart, setRangeStart] = useState('');
  const [rangeEnd, setRangeEnd] = useState('');
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const myPerson = ELIGIBLE_PEOPLE.find(p => p.email === user.email.toLowerCase());
  const others = myPerson
    ? ELIGIBLE_PEOPLE.filter(p => p.email !== myPerson.email && Math.abs(p.level - myPerson.level) <= 1)
    : [];

  const loadCalendar = useCallback(() => {
    const now = new Date();
    const start = fmtYMD(new Date(now.getFullYear(), now.getMonth(), 1));
    const end   = fmtYMD(new Date(now.getFullYear(), now.getMonth() + 3, 0));
    fetch(`${API_BASE}/calendar?start=${start}&end=${end}`).then(r => r.json()).then(setEntries).catch(() => {});
  }, []);

  useEffect(() => {
    loadCalendar();
    fetch(`${API_BASE}/mine?email=${encodeURIComponent(user.email)}`).then(r => r.json()).then(setMine).catch(() => {});
  }, [loadCalendar, user.email]);

  const handlePick = (ymd) => {
    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(ymd); setRangeEnd('');
    } else if (ymd < rangeStart) {
      setRangeStart(ymd);
    } else {
      setRangeEnd(ymd);
    }
  };

  const handleSubmit = async () => {
    setError('');
    const relief = ELIGIBLE_PEOPLE.find(p => p.email === reliefEmail);
    if (!relief || !rangeStart || !rangeEnd) { setError('Pick a relief and a full date range.'); return; }
    setSaving(true);
    try {
      const res = await fetch(API_BASE, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userEmail: user.email, userName: user.displayName,
          startDate: rangeStart, endDate: rangeEnd,
          reliefName: relief.name, reliefEmail: relief.email,
          coverageNote: note,
        }),
      });
      const data = await res.json();
      if (!res.ok) { setError(data.error || 'Something went wrong.'); return; }
      sendNotificationEmail({
        to: relief.email,
        subject: `${user.displayName} would like you to cover ${rangeStart} to ${rangeEnd}`,
        text: `${user.displayName} is on leave from ${rangeStart} to ${rangeEnd} and has named you as relief.${note ? `\n\nNote: ${note}` : ''}\n\nPlease log in to confirm or decline.`,
      });
      setRangeStart(''); setRangeEnd(''); setNote(''); setReliefEmail('');
      loadCalendar();
      fetch(`${API_BASE}/mine?email=${encodeURIComponent(user.email)}`).then(r => r.json()).then(setMine).catch(() => {});
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = async (id) => {
    await fetch(`${API_BASE}/${id}`, { method: 'DELETE' });
    setMine(m => m.filter(e => e.id !== id));
    loadCalendar();
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 10, padding: 24, width: 460, maxHeight: '85vh', overflowY: 'auto' }}
        onClick={e => e.stopPropagation()}>
        <div style={{ fontWeight: 700, fontSize: 16, color: '#333', marginBottom: 4 }}>Set Leave Roster</div>
        <div style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>
          No cases get reassigned automatically - this just tells your relief, and shows a note on CAS Dashboard while you're out.
        </div>

        {mine.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 6 }}>Your upcoming leave</div>
            {mine.map(m => (
              <div key={m.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 10px', background: '#f8f9fa', borderRadius: 6, marginBottom: 4, fontSize: 12 }}>
                <span>
                  {fmtDisplay(m.start_date.slice(0,10))} - {fmtDisplay(m.end_date.slice(0,10))} · {m.relief_name} covering
                  {m.status === 'pending_consent' && <span style={{ color: '#f0a500', marginLeft: 6 }}>(awaiting consent)</span>}
                  {m.status === 'confirmed' && <span style={{ color: '#1e7e34', marginLeft: 6 }}>(confirmed)</span>}
                  {m.status === 'declined' && <span style={{ color: '#c5221f', marginLeft: 6, fontWeight: 600 }}>(declined - choose a new relief)</span>}
                </span>
                <button type="button" onClick={() => handleCancel(m.id)} style={{ border: 'none', background: 'none', color: '#c5221f', cursor: 'pointer', fontSize: 11 }}>Cancel</button>
              </div>
            ))}
          </div>
        )}

        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 6 }}>Relief</div>
        <select value={reliefEmail} onChange={e => { setReliefEmail(e.target.value); setRangeStart(''); setRangeEnd(''); }}
          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, marginBottom: 12 }}>
          <option value="">Choose who's covering...</option>
          {others.map(p => <option key={p.email} value={p.email}>{p.name}</option>)}
        </select>

        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', marginBottom: 6 }}>Dates</div>
        <LeaveCalendarGrid entries={entries} reliefEmail={reliefEmail} rangeStart={rangeStart} rangeEnd={rangeEnd} onPick={handlePick} />
        {rangeStart && (
          <div style={{ fontSize: 12, color: '#333', marginTop: 8 }}>
            {fmtDisplay(rangeStart)}{rangeEnd ? ` - ${fmtDisplay(rangeEnd)}` : ' - pick end date'}
          </div>
        )}

        <div style={{ fontSize: 12, fontWeight: 700, color: '#555', margin: '12px 0 6px' }}>Coverage note (optional)</div>
        <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. covering KYC L1 reviews this week"
          style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #ddd', fontSize: 13, marginBottom: 12 }} />

        {error && <div style={{ background: '#fce8e6', color: '#c5221f', padding: '8px 10px', borderRadius: 6, fontSize: 12, marginBottom: 12 }}>{error}</div>}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={onClose} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Close</button>
          <button type="button" onClick={handleSubmit} disabled={saving}
            style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#c8102e', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>
            {saving ? 'Saving...' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
}

function LeaveConsentPrompt() {
  const { user } = useAuth();
  const [pending, setPending] = useState([]);

  useEffect(() => {
    fetch(`${API_BASE}/pending-consent?email=${encodeURIComponent(user.email)}`).then(r => r.json()).then(setPending).catch(() => {});
  }, [user.email]);

  const respond = async (id, accept) => {
    const item = pending.find(p => p.id === id);
    await fetch(`${API_BASE}/${id}/consent`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ accept }),
    });
    if (item) {
      const range = `${item.start_date.slice(0,10)} to ${item.end_date.slice(0,10)}`;
      sendNotificationEmail(accept ? {
        to: item.user_email,
        subject: `${user.displayName} confirmed - covering ${range}`,
        text: `${user.displayName} has confirmed they'll cover for you from ${range}. You're all set.`,
      } : {
        to: item.user_email,
        subject: `${user.displayName} declined to cover ${range}`,
        text: `${user.displayName} declined your leave-roster request for ${range}.\n\nPlease log in and choose a different relief.`,
      });
    }
    setPending(p => p.filter(e => e.id !== id));
  };

  if (pending.length === 0) return null;
  const item = pending[0];

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 600, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: '#fff', borderRadius: 10, padding: 24, width: 400 }}>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#333', marginBottom: 8 }}>Cover for {item.user_name}?</div>
        <div style={{ fontSize: 13, color: '#555', marginBottom: 12 }}>
          {item.user_name} would like you to cover from {fmtDisplay(item.start_date.slice(0,10))} to {fmtDisplay(item.end_date.slice(0,10))}.
          {item.coverage_note && <div style={{ marginTop: 6, fontStyle: 'italic', color: '#888' }}>"{item.coverage_note}"</div>}
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
          <button type="button" onClick={() => respond(item.id, false)} style={{ padding: '8px 16px', borderRadius: 6, border: '1px solid #ddd', background: '#fff', cursor: 'pointer', fontSize: 13 }}>Decline</button>
          <button type="button" onClick={() => respond(item.id, true)} style={{ padding: '8px 16px', borderRadius: 6, border: 'none', background: '#1e7e34', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}>Accept</button>
        </div>
      </div>
    </div>
  );
}

// Each page: const [leaveModalOpen, setLeaveModalOpen] = useState(false);
// then render <LeaveRosterEntry open={leaveModalOpen} onClose={...} /> once,
// and a "Set Leave Roster" button that calls setLeaveModalOpen(true). The
// consent prompt renders itself automatically, no page-side state needed.
export function LeaveRosterEntry({ open, onClose }) {
  return (
    <>
      <LeaveConsentPrompt />
      {open && <LeaveRosterModal onClose={onClose} />}
    </>
  );
}
